import { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Icon, MultiSelect } from '../../../../components';
import { useClickOutside, useGetUserPermissions, useSimActions } from '../../../../hooks';
import { useGetTagsQuery } from '../../../../services';
import { ProspectCategory, Tag } from '../../../../types';
import { getTagColorFromHex } from '../../../../utils';
import HoverableContent from './HoverableContent';

const MAX_TAG_WIDTH = 150;

interface SimulationDetailsTagsProps {
  isDraft: boolean;
  personaId: string;
  prospectCategory: ProspectCategory;
  prospectUserId: string;
  // Currently assigned tags for the simulation.
  currTags: Tag[];
}

const SimulationDetailsTags = ({
  isDraft,
  personaId,
  prospectCategory,
  prospectUserId,
  currTags,
}: SimulationDetailsTagsProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [inputTagIds, setInputTagIds] = useState<string[]>([]);

  // Ref for tracking the simulation ID when editing starts.
  // Used to ensure tags are applied to the correct simulation even if it changes during editing,
  // (ie. opening another simulation with changes applied to the current simulation).
  const personaIdToEditRef = useRef<string>(personaId);
  // Ref for handling click outside events.
  const inputRef = useRef<HTMLDivElement>(null);

  const { getCanUserEditSim } = useGetUserPermissions();
  const canUserEditSim = getCanUserEditSim(prospectCategory, isDraft, prospectUserId);

  const { runApplyTags } = useSimActions();

  // Fetch all available tags from the API.
  const { data: allTags = [] } = useGetTagsQuery();

  // Filter all tags to get input tag objects.
  // Used for immediate UI feedback when updating tags.
  const inputTags = allTags.filter((tag) => inputTagIds.includes(tag.id));

  const allTagOptions = allTags.map((tag) => ({ color: tag.color, label: tag.name, value: tag.id }));

  // Update the ref with the current personaId when editing starts.
  const onEdit = useCallback(() => {
    personaIdToEditRef.current = personaId;
    setIsEditing(true);
  }, [personaId]);

  const onSubmit = useCallback(() => {
    const currTagIds = currTags.map(({ id }) => id);
    const tagsToApply = inputTagIds.filter((id) => !currTagIds.includes(id));
    const tagsToRemove = currTagIds.filter((id) => !inputTagIds.includes(id));
    runApplyTags({
      id: personaIdToEditRef.current,
      tagsToApply,
      tagsToRemove,
      onError: () => setInputTagIds(currTagIds), // Reset the selected tags on error.
    }).then(() => setIsEditing(false)); // Close edit mode regardless of submission success.
  }, [currTags, inputTagIds, runApplyTags]);

  // Reset state when persona or current tags change.
  useEffect(() => {
    setIsEditing(false);
    setInputTagIds(currTags.map(({ id }) => id));
  }, [personaId, currTags]);

  // Handle click outside to submit changes.
  useClickOutside(inputRef, onSubmit);

  return isEditing ? (
    <MultiSelect
      ref={inputRef}
      wrap
      options={allTagOptions}
      selected={inputTagIds}
      onChange={(newTags) => setInputTagIds(newTags as string[])}
      placeholder="Select tags"
      onClose={onSubmit}
    />
  ) : (
    <HoverableContent
      disabled={!canUserEditSim}
      icon={Icon.EDIT}
      content={
        <div className="flex flex-wrap justify-end gap-2">
          {inputTags.map((tag) => (
            <Badge key={tag.id} label={tag.name} maxWidth={MAX_TAG_WIDTH} color={getTagColorFromHex(tag.color)} />
          ))}
        </div>
      }
      onClick={onEdit}
      tooltip="Edit tags"
      showButton={!inputTags.length}
    />
  );
};

export default SimulationDetailsTags;
